import { createSectionColumnId } from '../utils/proposalSectionColumns';
import { MAX_COPIES } from '../../utils/copies';

/**
 * Presse-papier de blocs — MAX_COPIES places, en mémoire, comme le
 * presse-papiers du classique (`utils/copies`). La copie la plus récente passe
 * en tête ; recopier un bloc déjà présent le remonte au lieu de le doubler.
 * Recharger la page ou ouvrir un autre document perd tout : rien n'est
 * persisté, c'est un presse-papier, pas une bibliothèque.
 *
 * Chaque copie apparaît dans la palette et se repose n'importe où, autant de
 * fois qu'on veut.
 */

/**
 * Tuiles palette : une par nature ET par place. Un type Puck ne transporte
 * rien d'autre que son nom : c'est lui qui dit, au drop, quelle copie poser.
 * La copie d'un Bloc ne va pas en colonne, d'où deux natures.
 */
export const CLIPBOARD_SECTION_TILE = 'ClipboardSectionTile';
export const CLIPBOARD_BOX_TILE = 'ClipboardBoxTile';

const tilesOf = (prefix) => Array.from({ length: MAX_COPIES }, (_, slot) => `${prefix}${slot}`);
export const CLIPBOARD_SECTION_TILES = tilesOf(CLIPBOARD_SECTION_TILE);
export const CLIPBOARD_BOX_TILES = tilesOf(CLIPBOARD_BOX_TILE);

export function clipboardTileType(entry, slot) {
  const prefix = entry?.item?.type === 'ProposalSection' ? CLIPBOARD_SECTION_TILE : CLIPBOARD_BOX_TILE;
  return `${prefix}${slot}`;
}

/** Place visée par une tuile du presse-papier, ou `null` pour un autre type. */
function clipboardSlotOf(componentType) {
  const slot = CLIPBOARD_SECTION_TILES.indexOf(componentType);
  return slot >= 0 ? slot : CLIPBOARD_BOX_TILES.indexOf(componentType);
}

/* ── Sous-arbre d'un bloc ─────────────────────────────────────────────────── */

/** Toutes les zones portées par ce bloc et ses descendants (colonnes, onglets). */
export function collectBlockSubtree(zones, rootId) {
  const all = zones && typeof zones === 'object' ? zones : {};
  const kept = {};
  const pending = [rootId];
  const seen = new Set();

  while (pending.length) {
    const ownerId = pending.shift();
    if (!ownerId || seen.has(ownerId)) continue;
    seen.add(ownerId);
    Object.entries(all).forEach(([key, blocks]) => {
      if (key.split(':')[0] !== ownerId) return;
      kept[key] = blocks;
      (Array.isArray(blocks) ? blocks : []).forEach((block) => {
        if (block?.props?.id) pending.push(block.props.id);
      });
    });
  }
  return kept;
}

/* ── Régénération des identifiants ────────────────────────────────────────── */

/** `section-hero-mf3x-a1b2` → `section-hero-<neuf>`, le préfixe parlant survit. */
function freshId(oldId) {
  const prefix = String(oldId).split('-').slice(0, -2).join('-');
  return `${prefix || 'block'}-${createSectionColumnId()}`;
}

/**
 * Tous les identifiants à renouveler : ceux des blocs, mais aussi ceux des
 * COLONNES — ils vivent à la fois dans `sectionColumns` et dans la clé de zone
 * `<blocId>:col-<colonneId>`. En garder un seul reproduirait la copie sur son
 * original (clés React en double, verrous de collaboration confondus).
 */
function collectRenamableIds(item, zones) {
  const ids = new Set();
  const addBlock = (block) => {
    if (block?.props?.id) ids.add(block.props.id);
  };
  addBlock(item);
  Object.entries(zones).forEach(([key, blocks]) => {
    const [ownerId, zoneName = ''] = key.split(':');
    ids.add(ownerId);
    if (zoneName.startsWith('col-')) ids.add(zoneName.slice('col-'.length));
    (Array.isArray(blocks) ? blocks : []).forEach(addBlock);
  });
  ids.delete('');
  return ids;
}

/** Remplace en profondeur toute chaîne exactement égale à un id renouvelé. */
function remapValue(value, map) {
  if (typeof value === 'string') return map.get(value) ?? value;
  if (Array.isArray(value)) return value.map((v) => remapValue(v, map));
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, remapValue(v, map)]),
    );
  }
  return value;
}

/**
 * La copie posée sur la page : mêmes props, identifiants neufs de bout en
 * bout. Sans ça, coller deux fois la même copie donnerait deux blocs portant le
 * même id — Puck les confondrait, et le verrou de collaboration de l'un
 * fermerait l'autre.
 */
export function instantiateClipboardBlock(entry) {
  const item = entry?.item;
  if (!item?.type) return null;
  const zones = entry.zones && typeof entry.zones === 'object' ? entry.zones : {};

  const map = new Map();
  collectRenamableIds(item, zones).forEach((id) => map.set(id, freshId(id)));

  const nextZones = {};
  Object.entries(zones).forEach(([key, blocks]) => {
    const [ownerId, zoneName = ''] = key.split(':');
    const nextOwner = map.get(ownerId) ?? ownerId;
    const nextZoneName = zoneName.startsWith('col-')
      ? `col-${map.get(zoneName.slice('col-'.length)) ?? zoneName.slice('col-'.length)}`
      : zoneName;
    nextZones[`${nextOwner}:${nextZoneName}`] = (Array.isArray(blocks) ? blocks : [])
      .map((block) => remapValue(block, map));
  });

  return { item: remapValue(item, map), zones: nextZones };
}

/* ── Les places ──────────────────────────────────────────────────────────── */

let copies = [];
const listeners = new Set();

function publish() {
  listeners.forEach((fn) => fn());
}

/** Les copies en attente, la plus récente en tête (référence stable entre deux changements). */
export function getClipboardCopies() {
  return copies;
}

export function subscribeBlockClipboard(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * La copie passe en tête, identifiée par l'id du bloc copié (cf. `pushCopy`
 * du classique) : au-delà de MAX_COPIES, la plus ancienne tombe.
 */
export function copyBlockToClipboard({ label, item, zones }) {
  if (!item?.type) return null;
  const sourceId = item.props?.id ?? null;
  const entry = {
    sourceId,
    label: String(label || '').trim() || item.type,
    item,
    zones: collectBlockSubtree(zones, sourceId),
  };
  copies = [
    entry,
    ...copies.filter((copy) => sourceId == null || copy.sourceId !== sourceId),
  ].slice(0, MAX_COPIES);
  publish();
  return entry;
}

/** Retire une copie : sans ça, il faudrait copier autre chose pour la faire tomber. */
export function removeClipboardCopy(sourceId) {
  const next = copies.filter((copy) => copy.sourceId !== sourceId);
  if (next.length === copies.length) return;
  copies = next;
  publish();
}

/** Vidé au changement de document — une copie ne traverse pas les proposals. */
export function clearBlockClipboard() {
  if (!copies.length) return;
  copies = [];
  publish();
}

/* ── Pose de la copie ─────────────────────────────────────────────────────── */

/**
 * Drop d'une tuile du presse-papier ? Contrairement aux macros, la copie d'une
 * box peut atterrir dans une colonne : la zone visée fait partie du résultat,
 * comme la place de la copie à poser.
 * @returns {null|{componentType: string, slot: number, index: number, zone: string}}
 */
export function getClipboardInsert(action) {
  if (action?.type !== 'insert') return null;
  const { componentType } = action;
  const slot = clipboardSlotOf(componentType);
  if (slot < 0) return null;
  return {
    componentType, slot, index: action.destinationIndex, zone: action.destinationZone,
  };
}

const isRootZone = (zone) => zone === 'root:default-zone' || zone === 'default-zone';

/** Remplace la tuile fraîchement insérée par la copie, à la racine ou en colonne. */
export function expandClipboardBlockAtIndex(puckData, { index, zone }, entry) {
  const posed = instantiateClipboardBlock(entry);
  if (!posed) return puckData;

  const zones = puckData?.zones && typeof puckData.zones === 'object'
    ? { ...puckData.zones, ...posed.zones }
    : { ...posed.zones };

  if (isRootZone(zone)) {
    const content = puckData?.content;
    if (!Array.isArray(content) || !content[index]) return puckData;
    const nextContent = [...content];
    nextContent[index] = posed.item;
    return { ...puckData, content: nextContent, zones };
  }

  const target = puckData?.zones?.[zone];
  if (!Array.isArray(target) || !target[index]) return puckData;
  const nextTarget = [...target];
  nextTarget[index] = posed.item;
  return { ...puckData, zones: { ...zones, [zone]: nextTarget } };
}

/**
 * Copie retirée entre le glisser et le drop : la tuile n'ayant aucun
 * rendu, la laisser cacherait un bloc invisible dans les données.
 */
export function removeClipboardTile(puckData, { index, zone }) {
  if (isRootZone(zone)) {
    const content = puckData?.content;
    if (!Array.isArray(content) || !content[index]) return puckData;
    return { ...puckData, content: content.filter((_, i) => i !== index) };
  }
  const target = puckData?.zones?.[zone];
  if (!Array.isArray(target) || !target[index]) return puckData;
  return {
    ...puckData,
    zones: { ...puckData.zones, [zone]: target.filter((_, i) => i !== index) },
  };
}
